import { type components } from '~/common/types/kpc.d';
import { type Document } from '~/common/types/document/document';

import { type EntityType, Language } from '~/common/enums';

import createRestClient from '../restClient';

export type DocumentResponse = Omit<Document, 'id'> & {
  id: string;
};

export type UploadBatchDocumentRequest = {
  entityId?: string;
  entityType?: EntityType;
  files: (File | Blob)[];
  fileIds?: string[];
  parentEntityId: string;
  parentEntityType: EntityType;
  storeUntil?: string;
  languages?: Language[];
};

export type DownloadMultipleBatchAttachmentsRequest =
  components['schemas']['KeypointConnect.Messaging.Backend.Rest.Batches.V1.DTOs.Requests.DownloadMultipleBatchMessageAttachmentsRequest'];

const clients = {
  base: createRestClient(),
  raw: createRestClient(null, true),
};

const downloadBatchMessageAttachments = ({
  request,
}: {
  request: { requests: DownloadMultipleBatchAttachmentsRequest; shouldMerge: boolean };
}) =>
  clients.raw.post('/communication/batch-messages/download-attachments', request, {
    responseType: 'blob',
  });

const uploadBatchMessageAttachments = ({
  entityId,
  entityType,
  files,
  fileIds,
  parentEntityId,
  parentEntityType,
  storeUntil,
  languages = [Language.Dutch, Language.English, Language.French],
}: UploadBatchDocumentRequest): Promise<DocumentResponse[]> => {
  const data = new FormData();

  if (entityId && entityType) {
    data.set('entityId', entityId);
    data.set('entityType', entityType);
  }

  fileIds?.forEach((id) => {
    data.append('fileIds', id);
  });

  data.set('parentEntityId', parentEntityId);
  data.set('parentEntityType', parentEntityType);

  if (storeUntil) {
    data.set('storeUntil', storeUntil);
  }

  files.forEach((file) => {
    data.append('files', file);
  });

  languages.forEach((language) => {
    data.append('languages', language);
  });

  return clients.base.postForm('/document/attachments', data);
};

export default {
  downloadBatchMessageAttachments,
  uploadBatchMessageAttachments,
};
